import type { ReactNode } from "react";
import { View } from "react-native";
import { Button, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

type RecommendedCardProps = {
	variant: "base" | "preferred" | "upsell";
	icon: ReactNode;
	title: string;
	description: string;
	ctaLabel: string;
	onPress: () => void;
};

export const RecommendedCard = ({
	variant,
	icon,
	title,
	description,
	ctaLabel,
	onPress,
}: RecommendedCardProps) => {
	const theme = useAppTheme();

	const getVariantStyle = () => {
		switch (variant) {
			case "base":
				return {
					backgroundColor: theme.colors.surfaceContainerLow,
					borderColor: theme.colors.primaryContainer,
					badge: null,
					buttonColor: theme.colors.secondaryContainer,
					buttonTextColor: theme.colors.onSecondaryContainer,
				};
			case "preferred":
				return {
					backgroundColor: "#EFFDF4",
					borderColor: "#C8E5C7",
					badge: { label: "Recommended", icon: null },
					buttonColor: theme.colors.success.main,
					buttonTextColor: theme.colors.white,
				};
			case "upsell":
				return {
					backgroundColor: undefined,
					borderColor: "#C8E5C7",
					badge: { label: "Premium", icon: "lock" },
					buttonColor:
						(theme as { palette?: { premium?: string } }).palette?.premium ??
						"#d4af37",
					buttonTextColor: theme.colors.white,
				};
			default:
				return {
					backgroundColor: theme.colors.surfaceContainerLow,
					borderColor: theme.colors.primaryContainer,
					badge: null,
					buttonColor: theme.colors.secondaryContainer,
					buttonTextColor: theme.colors.onSecondaryContainer,
				};
		}
	};

	const variantStyle = getVariantStyle();

	const getTextColor = () => {
		switch (variant) {
			case "preferred":
				return "#14532D";
			case "upsell":
				return "#735A01";
			default:
				return theme.colors.onSurface;
		}
	};

	const textColor = getTextColor();

	return (
		<View
			style={{
				borderRadius: 12,
				borderWidth: 1,
				borderColor: variantStyle.borderColor,
				backgroundColor: variantStyle.backgroundColor,
				padding: 16,
				gap: 8,
				overflow: "hidden",
			}}
			testID={`recommended-card-${variant}`}
		>
			<Box flexDirection="row" alignItems="center" gap={8}>
				{icon}
				<Text variant="titleSmall" style={{ flex: 1, color: textColor }}>
					{title}
				</Text>
			</Box>

			<Text variant="bodySmall" style={{ color: textColor }}>
				{description}
			</Text>

			<Button
				mode="contained-tonal"
				onPress={onPress}
				buttonColor={variantStyle.buttonColor}
				textColor={variantStyle.buttonTextColor}
				style={{ alignSelf: "flex-start" }}
				testID={`cta-button-${variant}`}
			>
				{ctaLabel}
			</Button>
		</View>
	);
};
